import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EvaluationPlanAttachment } from '../../types/evaluationPlan';
import {
  attachmentSupportsShareLinkActions,
  isWebDocumentAttachment,
  triggerEvaluationPlanShareLinkDownload,
} from '../../utils/evaluationPlanAttachment';
import {
  computeZoomAwareAnchorCenteredPopupLayout,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../../utils/pageZoom';

const HOVER_CLOSE_DELAY_MS = 160;
const POPOVER_GAP_PX = 8;
const VIEWPORT_PAD_PX = 8;
const POPOVER_ESTIMATED_HEIGHT_PX = 44;
const POPOVER_ESTIMATED_WIDTH_PX = 108;

type PopoverPlacement = 'above' | 'below';

/** 공유링크·웹문서·파일 등록됨: 클릭·호버 시 미니 모달 / 미등록: 등록 모달 */
const EvaluationPlanShareLinkActionPopover: React.FC<{
  attachment: EvaluationPlanAttachment | null | undefined;
  onRegister?: () => void;
  onOpenDocument?: () => void;
  onOpenPreview?: () => void;
  className?: string;
  /** 모달 안에서 쓸 때: 모달 위(z-index)·버튼 바로 아래에 표시 */
  inModal?: boolean;
  children: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
}> = ({ attachment, onRegister, onOpenDocument, onOpenPreview, className, inModal = false, children }) => {
  const popoverZIndex = inModal ? 10460 : 2300;
  const menuId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties | null>(null);
  const [placement, setPlacement] = useState<PopoverPlacement>('above');

  const shareLinkAttachment = attachmentSupportsShareLinkActions(attachment) ? attachment : null;
  const webDocumentAttachment = isWebDocumentAttachment(attachment) ? attachment : null;
  const hasActionPopover =
    !!shareLinkAttachment || (!!webDocumentAttachment && !!onOpenDocument) || !!onOpenPreview;

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  const updatePopoverPosition = useCallback(() => {
    const anchorEl = anchorRef.current;
    const popoverEl = popoverRef.current;
    if (!anchorEl || !open) return;

    const next = computeZoomAwareAnchorCenteredPopupLayout({
      anchorRect: anchorEl.getBoundingClientRect(),
      popoverWidth: popoverEl?.offsetWidth || POPOVER_ESTIMATED_WIDTH_PX,
      popoverHeight: popoverEl?.offsetHeight || POPOVER_ESTIMATED_HEIGHT_PX,
      gap: inModal ? 4 : POPOVER_GAP_PX,
      pad: VIEWPORT_PAD_PX,
      preferredPlacement: inModal ? 'below' : undefined,
    });
    setPlacement(next.placement);
    setPopoverStyle({
      position: 'fixed',
      top: next.top,
      left: next.left,
      zIndex: popoverZIndex,
    });
  }, [open, inModal, popoverZIndex]);

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPopoverStyle(null);
      return;
    }
    updatePopoverPosition();
    const raf = window.requestAnimationFrame(() => updatePopoverPosition());
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, updatePopoverPosition);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, updatePopoverPosition);
    };
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const handleDownload = () => {
    if (!shareLinkAttachment) return;
    triggerEvaluationPlanShareLinkDownload(shareLinkAttachment);
    setOpen(false);
  };

  const handleOpenDocument = () => {
    if (!webDocumentAttachment || !onOpenDocument) return;
    onOpenDocument();
    setOpen(false);
  };

  const handleOpenPreview = () => {
    if (!onOpenPreview) return;
    onOpenPreview();
    setOpen(false);
  };

  const childOnClick = children.props.onClick;

  if (!hasActionPopover) {
    if (!onRegister) return children;
    return React.cloneElement(children, {
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        children.props.onClick?.(event);
        if (!event.defaultPrevented) onRegister();
      },
    });
  }

  const popoverMenu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={popoverRef}
            id={menuId}
            className={`cp-evaluation-plan-share-link-popover is-${placement}`}
            style={popoverStyle ?? { position: 'fixed', visibility: 'hidden', zIndex: popoverZIndex }}
            role="menu"
            aria-label={shareLinkAttachment ? '공유링크 다운로드' : '문서 작업'}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {shareLinkAttachment ? (
              <button
                type="button"
                className="cp-evaluation-plan-share-link-popover-item"
                role="menuitem"
                onClick={handleDownload}
              >
                다운로드
              </button>
            ) : null}
            {webDocumentAttachment && onOpenDocument ? (
              <button
                type="button"
                className="cp-evaluation-plan-share-link-popover-item"
                role="menuitem"
                onClick={handleOpenDocument}
              >
                문서 열기
              </button>
            ) : null}
            {onOpenPreview ? (
              <button
                type="button"
                className="cp-evaluation-plan-share-link-popover-item"
                role="menuitem"
                onClick={handleOpenPreview}
              >
                미리보기
              </button>
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        ref={anchorRef}
        className={['cp-evaluation-plan-share-link-popover-anchor', className].filter(Boolean).join(' ')}
        onMouseEnter={() => {
          clearCloseTimer();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
      >
        {React.cloneElement(children, {
          'aria-haspopup': 'menu',
          'aria-expanded': open,
          'aria-controls': open ? menuId : undefined,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            clearCloseTimer();
            setOpen((prev) => !prev);
          },
        })}
      </div>
      {popoverMenu}
    </>
  );
};

export default EvaluationPlanShareLinkActionPopover;
